/**
 * MarketCapPage — Account market cap calculator (§2.19 账户市值统计).
 *
 * Shows:
 *   1. Supply summary for all 16 tokens
 *   2. Market cap = total_uo × uo_price with price simulator
 *   3. Top holders table (filterable by token)
 */
import { useState } from 'react';
import { Card, Col, Row, Statistic, Table, Tag, Select, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { DollarOutlined, TeamOutlined, BankOutlined } from '@ant-design/icons';
import { api } from '../../api/client';
import { TOKEN_IDS, TOKEN_META, type TokenId } from '@uo/shared';

export default function MarketCapPage() {
  const [holderAsset, setHolderAsset] = useState<string>('uo');

  const { data: summary } = useQuery({
    queryKey: ['market-cap-summary'],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/market-cap/summary');
      return res.data;
    },
    staleTime: 60_000,
  });

  const { data: topHolders } = useQuery({
    queryKey: ['market-cap-top', holderAsset],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/market-cap/top-holders', {
        params: { asset: holderAsset, limit: 20 },
      });
      return res.data;
    },
    staleTime: 60_000,
  });

  const { data: simulations } = useQuery({
    queryKey: ['market-cap-simulate'],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/market-cap/simulate', {
        params: { prices: '0.1,0.5,1,2,5,10,50,100' },
      });
      return res.data;
    },
    staleTime: 60_000,
  });

  const fmt = (v: string | undefined) => {
    if (!v) return '0';
    const n = parseFloat(v);
    return isNaN(n) ? '0' : n.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  };

  const supplyData = TOKEN_IDS.map((tok: TokenId) => ({
    key: tok,
    symbol: TOKEN_META[tok].symbol,
    name: TOKEN_META[tok].name,
    supply: summary?.supplies?.[tok] ?? '0',
  })).filter((r) => parseFloat(r.supply) > 0);

  const supplyColumns = [
    { title: '代币', dataIndex: 'symbol', width: 80, render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: '名称', dataIndex: 'name', width: 120 },
    { title: '总量', dataIndex: 'supply', width: 180, render: (v: string) => fmt(v) },
  ];

  const holderColumns = [
    { title: '#', width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户名', dataIndex: 'username', width: 140 },
    { title: '等级', dataIndex: 'level_code', width: 80 },
    { title: '余额', dataIndex: 'balance', width: 160, render: (v: string) => fmt(v) },
    {
      title: '虚拟',
      dataIndex: 'is_bot',
      width: 70,
      render: (v: boolean) => v ? <Tag color="orange">是</Tag> : null,
    },
  ];

  const simColumns = [
    { title: 'ORB单价', dataIndex: 'price', width: 100, render: (v: string) => `¥${v}` },
    { title: '总市值', dataIndex: 'market_cap', width: 180, render: (v: string) => `¥${fmt(v)}` },
  ];

  return (
    <div>
      {/* Top summary */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="当前ORB单价"
              value={summary?.uo_price ?? '—'}
              prefix={<DollarOutlined />}
              suffix="¥"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="ORB总市值" value={fmt(summary?.market_cap)} prefix={<BankOutlined />} suffix="¥" />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="总用户" value={summary?.total_users ?? 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="活跃用户" value={summary?.active_users ?? 0} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        {/* Token supply table */}
        <Col span={12}>
          <Card title="各代币总供应量" size="small">
            <Table
              dataSource={supplyData}
              columns={supplyColumns}
              rowKey="key"
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
            />
          </Card>
        </Col>

        {/* Price simulator */}
        <Col span={12}>
          <Card title="ORB市值模拟" size="small">
            <Table
              dataSource={simulations ?? []}
              columns={simColumns}
              rowKey="price"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* Top holders */}
      <Card
        title="持仓排行 (Top 20)"
        size="small"
        extra={
          <Select
            value={holderAsset}
            onChange={(v) => setHolderAsset(v)}
            style={{ width: 160 }}
            options={TOKEN_IDS.map((t: TokenId) => ({
              value: t,
              label: `${TOKEN_META[t].symbol} — ${TOKEN_META[t].name}`,
            }))}
          />
        }
      >
        <Table
          dataSource={topHolders ?? []}
          columns={holderColumns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
}
